import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  DialogContentText,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { INotaFiscal } from "../../../../../../types/api/fiscal/detalhe-nfe-entrada";
import { NfItemTaxa } from ".";
import { Controller, FormProvider, useForm } from "react-hook-form";
import SelectPlanoContas from "../../../../../../components/SelectsSimpleApiQuery/SelectPlanoContas";

export function DialogAprovarNotaFiscal({
  open,
  fiscal,
  taxasItens = [],
  handleClose,
  onSuccess
}: DialogAprovarNotaFiscalProps) {

  const queryClient = useQueryClient();
  const methods = useForm<TParamsAprovarNotaFiscal>({ defaultValues: defaultValuesForm });
  const { getValues, handleSubmit, control, setValue } = methods;

  const { mutate: aprovar, isLoading } = useMutation({
    mutationKey: "aprovacao-fiscal",
    mutationFn: (inputs: TParamsAprovarNotaFiscal) =>
      axiosClient.post("/fiscal/aprovacao-fiscal", inputs),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Erro ao aprovar nota fiscal. Tente novamente.', { variant: 'error' });
    },
    onSuccess: () => {
      enqueueSnackbar('Nota fiscal aprovada com sucesso!', { variant: 'success' });
      queryClient.invalidateQueries(['nota-fiscal', fiscal.id]);
      onSuccess();
    },
  });

  const onSubmit = (data: TParamsAprovarNotaFiscal) => {
    const payload: TParamsAprovarNotaFiscal = {
      id: fiscal.id,
      fluxo: 'aprovar',
      criar_titulos: data.criar_titulos,
      conta_contabil: data.conta_contabil.codigo_contabil,
      ...(data.conta_contabil?.centro_custo && {
        conta_contabil_custo: data.conta_contabil.centro_custo.id,
      })
    };

    if (taxasItens.length > 0) {
      payload.taxasItens = taxasItens;
    }

    aprovar(payload);
  };

  const opcoesRadio = [
    { label: "Sim", value: "Sim" },
    { label: "Não", value: "Não" }
  ];

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Deseja aprovar a Nota Fiscal?
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <DialogContentText>
                Será realizada a aprovação da nota fiscal {fiscal.numero_nota}
              </DialogContentText>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="conta_contabil"
                control={control}
                rules={{ required: "Campo obrigatório" }}
                render={({ field, fieldState: { error, invalid } }) => (
                  <>
                    <SelectPlanoContas
                      inputLabel="Conta contábil"
                      value={field.value}
                      required
                      error={invalid}
                      onChangeValue={(valor) => {
                        if (valor.sistema_xy === 'D-PAG') {
                          setValue('criar_titulos', 'Sim');
                        }
                        field.onChange(valor);
                      }}
                      extraParams={{ tipo: "Analítica" }}
                    />
                    {invalid && <FormHelperText error>{error?.message}</FormHelperText>}
                    {field.value?.sistema_xy === 'D-PAG' && (
                      <FormHelperText error>
                        A conta selecionada é do tipo D-PAG, portanto os títulos serão criados automaticamente.
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name='criar_titulos'
                control={control}
                rules={{ required: 'Campo obrigatório' }}
                render={({ field, fieldState: { error, invalid } }) => (
                  <FormControl {...field} required error={invalid}>
                    <FormLabel id="criar_titulos">Criar títulos?</FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="Criar títulos?"
                      value={getValues('criar_titulos') || "Não"}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      {
                        opcoesRadio.map((opcao, index) => (
                          <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                        ))
                      }
                    </RadioGroup>
                    {invalid && <FormHelperText error>{error?.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="primary"
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            Aprovar
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}

interface DialogAprovarNotaFiscalProps {
  open: boolean;
  handleClose: () => void;
  onSuccess: () => void;
  fiscal: INotaFiscal;
  taxasItens?: NfItemTaxa[];
}

export type TParamsAprovarNotaFiscal = {
  id: number;
  fluxo: string;
  criar_titulos: string;
  conta_contabil: any
  conta_contabil_custo: number | null;
  taxasItens: NfItemTaxa[]
}

const defaultValuesForm: TParamsAprovarNotaFiscal = {
  id: 0,
  fluxo: 'aprovar',
  criar_titulos: "Não",
  conta_contabil: "",
  conta_contabil_custo: null,
  taxasItens: []
}